import { useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useContentDoc, SectionHeader, Loading, Field, PrimaryBtn, inputCls } from "./components";

export default function SettingsSection() {
  const { content, setContent, save } = useContentDoc();
  const [saving, setSaving] = useState(false);

  if (!content) return <Loading />;

  const set = (k) => (e) => setContent({ ...content, [k]: e.target.value });

  const saveAll = async () => {
    setSaving(true);
    try {
      await save(content);
      toast.success("Settings saved! The website is updated.");
    } catch {
      toast.error("Could not save. Please try again.");
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-settings-section">
      <SectionHeader title="Website Settings" subtitle="Basic program information shown across the whole website — header, footer, and contact page." />

      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="font-condensed text-2xl font-bold uppercase text-[#0F4C2A] mb-5">Program Info</h2>
          <div className="space-y-4">
            <Field label="Team Name" hint="Shown in the header at the top of every page">
              <input className={inputCls} value={content.team_name || ""} onChange={set("team_name")} data-testid="settings-team-name" />
            </Field>
            <Field label="Home Field">
              <input className={inputCls} value={content.field_name || ""} onChange={set("field_name")} data-testid="settings-field-name" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="School Name">
                <input className={inputCls} value={content.contact_school || ""} onChange={set("contact_school")} data-testid="settings-school" />
              </Field>
              <Field label="School Address">
                <input className={inputCls} value={content.contact_address || ""} onChange={set("contact_address")} data-testid="settings-address" />
              </Field>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="font-condensed text-2xl font-bold uppercase text-[#0F4C2A] mb-5">Contact Information</h2>
          <div className="space-y-4">
            <Field label="Contact Person">
              <input className={inputCls} value={content.contact_coach || ""} onChange={set("contact_coach")} data-testid="settings-contact-coach" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Contact Email">
                <input type="email" className={inputCls} value={content.contact_email || ""} onChange={set("contact_email")} data-testid="settings-contact-email" />
              </Field>
              <Field label="Contact Phone">
                <input className={inputCls} value={content.contact_phone || ""} onChange={set("contact_phone")} data-testid="settings-contact-phone" />
              </Field>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="font-condensed text-2xl font-bold uppercase text-[#0F4C2A] mb-5">Social Media (optional)</h2>
          <div className="space-y-4">
            <Field label="Instagram link" hint="Paste the full link, e.g. https://instagram.com/norwalkbears — leave blank to hide">
              <input className={inputCls} value={content.social_instagram || ""} onChange={set("social_instagram")} placeholder="https://instagram.com/..." data-testid="settings-instagram" />
            </Field>
            <Field label="X / Twitter link">
              <input className={inputCls} value={content.social_twitter || ""} onChange={set("social_twitter")} placeholder="https://x.com/..." data-testid="settings-twitter" />
            </Field>
            <Field label="Facebook link">
              <input className={inputCls} value={content.social_facebook || ""} onChange={set("social_facebook")} placeholder="https://facebook.com/..." data-testid="settings-facebook" />
            </Field>
          </div>
        </div>
      </div>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <PrimaryBtn onClick={saveAll} disabled={saving} className="shadow-xl px-8 py-4 text-base" data-testid="settings-save-button">
          <Save size={17} /> {saving ? "Saving..." : "Save Settings"}
        </PrimaryBtn>
      </div>
    </div>
  );
}
